import { apiBaseUrl, TOKEN_STORAGE_KEY } from './config';
import { decodeJwtExp, fireExpiry, scheduleExpiry } from './sessionExpiry';

// Sliding-session keep-alive (System Settings → Session, wired 2026-08-23).
//
// The rule the owner specified: someone USING the portal is never logged
// out mid-work — the JWT silently refreshes while there is real activity —
// but a workstation left alone re-asks for the password after the
// configured idle timeout, and every session hard-stops at the absolute
// TTL (server-enforced: /auth/refresh refuses past it) no matter how
// active it is.
//
// Mechanics:
//   * pointer/keyboard/wheel/touch listeners (passive, throttled) stamp
//     the last-activity instant;
//   * a 30 s ticker: idle ≥ policy.idleTimeoutMinutes → sign out with
//     reason=idle; otherwise, when the token is inside its refresh margin
//     AND there has been recent activity, POST /auth/refresh and store the
//     new token (re-arming the eager expiry timer);
//   * policy comes from GET /auth/session-policy (cached, re-fetched
//     every 15 min so settings-page changes apply without a reload).

type SessionPolicy = {
  fullJwtTtlMinutes: number;
  idleTimeoutMinutes: number;
  absoluteTtlHours: number;
};

const ACTIVITY_THROTTLE_MS = 5_000;
const TICK_MS = 30_000;
const POLICY_REFETCH_MS = 15 * 60_000;

let installed = false;
let lastActivityMs = Date.now();
let lastActivityStamp = 0;
let policy: SessionPolicy | null = null;
let policyFetchedAt = 0;
let refreshInFlight = false;

function noteActivity(): void {
  const now = Date.now();
  if (now - lastActivityStamp < ACTIVITY_THROTTLE_MS) return;
  lastActivityStamp = now;
  lastActivityMs = now;
}

/** Exposed for tests. */
export function _setLastActivityForTests(ms: number): void { lastActivityMs = ms; }
export function _resetForTests(): void {
  policy = null; policyFetchedAt = 0; lastActivityMs = Date.now(); refreshInFlight = false;
}

async function fetchPolicy(token: string): Promise<void> {
  try {
    const r = await fetch(`${apiBaseUrl}/auth/session-policy`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      policy = (await r.json()) as SessionPolicy;
      policyFetchedAt = Date.now();
    }
  } catch { /* offline — keep the previous policy */ }
}

async function refreshToken(token: string): Promise<void> {
  if (refreshInFlight) return;
  refreshInFlight = true;
  try {
    const r = await fetch(`${apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      const body = (await r.json()) as { token?: string };
      if (body.token) {
        window.sessionStorage.setItem(TOKEN_STORAGE_KEY, body.token);
        scheduleExpiry(body.token);
      }
    } else if (r.status === 401) {
      // Absolute TTL reached (or the token was revoked server-side): the
      // session may not slide any further — full re-login.
      fireExpiry('expired');
    }
  } catch { /* transient network failure — the next tick retries */ }
  finally { refreshInFlight = false; }
}

export async function keepaliveTick(): Promise<void> {
  const token = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return;

  if (!policy || Date.now() - policyFetchedAt > POLICY_REFETCH_MS) {
    await fetchPolicy(token);
  }
  const p = policy;
  if (!p) return; // policy endpoint unreachable — the eager exp timer still guards

  // Idle: the workstation has sat untouched past the configured window —
  // force a password re-entry (owner rule).
  if (Date.now() - lastActivityMs >= p.idleTimeoutMinutes * 60_000) {
    fireExpiry('idle');
    return;
  }

  // Active: slide the session before the token runs out. Margin = a third
  // of the TTL, capped at 10 minutes, so short TTLs refresh promptly and
  // long ones don't chatter.
  const expMs = decodeJwtExp(token);
  if (expMs === null) return;
  const marginMs = Math.min(10 * 60_000, (p.fullJwtTtlMinutes * 60_000) / 3);
  if (expMs - Date.now() <= marginMs) {
    await refreshToken(token);
  }
}

/** Install listeners + the ticker. Safe to call repeatedly. */
export function installSessionKeepalive(): void {
  if (typeof window === 'undefined' || installed) return;
  installed = true;

  for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const) {
    window.addEventListener(ev, noteActivity, { passive: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void keepaliveTick();
  });

  window.setInterval(() => { void keepaliveTick(); }, TICK_MS);
  void keepaliveTick();
}
